import * as B from '@blueprintjs/core';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import 'app/styles/blueprintShim.scss';
import 'app/styles/global.css';
// tokens.scss is auto-@use'd in every other .scss via Vite's
// preprocessorOptions.scss.additionalData; no global import needed.
import 'app/styles/animations.scss';
import {Chart} from 'chart.js';
import React from 'react';
import {DndProvider} from 'react-dnd';
import {HTML5Backend} from 'react-dnd-html5-backend';
import {Helmet} from 'react-helmet';
import {BrowserRouter} from 'react-router-dom';
import {QueryParamProvider} from 'use-query-params';
import {ReactRouter6Adapter} from 'use-query-params/adapters/react-router-6';

import './assets/favicon/apple-touch-icon.png';
import './assets/favicon/favicon-96x96.png';
import './assets/favicon/favicon.ico';
import './assets/favicon/favicon.svg';
import './assets/favicon/site.webmanifest';

import GlobalErrorBoundary from 'app/components/ErrorBoundaries';
import Loading from 'app/components/Loading';
import PushNotification, {PushNotificationProvider} from 'app/components/Notification';
import PendingAgentRunsMonitor from 'app/modules/AgentRuns/PendingAgentRunsMonitor';
import {PendingAgentRunsProvider} from 'app/modules/AgentRuns/PendingAgentRunsProvider';
import {CartProvider, CartUndoProvider} from 'app/modules/Cart';
import VersionUpdateMonitor from 'app/modules/Realtime/VersionUpdateMonitor';
import AuthProvider, {WithAuth} from 'app/providers/AuthProvider';
import OrgUsersProvider from 'app/providers/OrgUsersProvider';
import ProjectsProvider from 'app/providers/ProjectsProvider';
import FirebaseProvider from 'app/tools/Firebase';
import HelpScout from 'app/tools/Helpscout';
import Hubspot from 'app/tools/Hubspot';
import {SentrySessionReplayListener, SentryUserListener} from 'app/tools/Sentry';
import {isProd} from 'app/utils/envUtils';
import featureFlags from 'app/utils/featureFlags';

import {makeChartJsRegistrations} from './ChartJsRegistrations';
import {NavSidebarProvider} from './components/NavSidebar/NavSidebarProvider';
import AppRouter from './routes';
import Analytics from './tools/Analytics';

B.FocusStyleManager.onlyShowFocusOnTabs();

// We need to do this globally because you can’t set the font for a particular
// chart (you have to set it for the individual parts of the chart, which is
// annoying.)
//
// TODO(fiona): This could be nicer.
Chart.defaults.font = {
  family: "'Lato', Helvetica, Arial, sans-serif",
  size: 14,
  lineHeight: 1,
  style: 'normal',
};

/** Register all chartJs plugins, elements, and controllers in a centralized location. If you are working with
 * chartJs and get an error a la "{x} is not a registered controller", it's likely you need to
 * register something here. Registration for each element only needs to happen once in the app so we are
 * centralizing here rather than registering individual elements by chart. Everything in this list is used.*/
makeChartJsRegistrations();

class App extends React.Component {
  render() {
    const queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          /**
           * if we don't set a stale time, the data will always be considered stale and get refetched
           * every time we focus the window (ie, after switching browser tabs) or remount a component.
           * We can override those with refetchOnWindowFocus and refetchOnMount but for now, setting
           * a stale threshold seems like the best compromise.
           */
          staleTime: 5 * 60 * 1000,
        },
      },
    });

    return (
      <GlobalErrorBoundary>
        <Helmet>
          <title>Lens</title>
        </Helmet>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <QueryParamProvider adapter={ReactRouter6Adapter}>
              <DndProvider backend={HTML5Backend}>
                <FirebaseProvider>
                  <B.HotkeysProvider dialogProps={{globalGroupName: 'Keyboard Shortcuts'}}>
                    <NavSidebarProvider>
                      <PushNotificationProvider>
                        <VersionUpdateMonitor />
                        <AuthProvider renderLoading={() => <Loading />}>
                          <WithAuth>
                            {(auth) => {
                              const organizationId =
                                auth.status === 'logged in'
                                  ? (auth.currentOrganization?.get('id') ?? null)
                                  : null;
                              const userId =
                                auth.status === 'logged in'
                                  ? (auth.profile?.get('id') ?? null)
                                  : null;
                              const canRunAgent =
                                auth.status === 'logged in' &&
                                !!auth.currentOrganization &&
                                !!auth.profile &&
                                featureFlags.AGENT_RUN_CREATION(
                                  auth.currentOrganization,
                                  auth.profile
                                );

                              return (
                                <PendingAgentRunsProvider
                                  firebaseUid={
                                    auth.status === 'logged in' ? auth.firebaseUid : null
                                  }
                                  organizationId={organizationId}
                                  canRunAgent={canRunAgent}
                                >
                                  <PushNotification />

                                  <SentryUserListener />
                                  <SentrySessionReplayListener />
                                  {isProd && <Hubspot />}
                                  {isProd && <HelpScout />}
                                  <Analytics />
                                  {auth.status === 'logged in' && <PendingAgentRunsMonitor />}

                                  <ProjectsProvider organizationId={organizationId}>
                                    <OrgUsersProvider organizationId={organizationId}>
                                      <CartProvider userId={userId} organizationId={organizationId}>
                                        <CartUndoProvider>
                                          <AppRouter />
                                        </CartUndoProvider>
                                      </CartProvider>
                                    </OrgUsersProvider>
                                  </ProjectsProvider>
                                </PendingAgentRunsProvider>
                              );
                            }}
                          </WithAuth>
                        </AuthProvider>
                      </PushNotificationProvider>
                    </NavSidebarProvider>
                  </B.HotkeysProvider>
                </FirebaseProvider>
              </DndProvider>
            </QueryParamProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </GlobalErrorBoundary>
    );
  }
}

export default App;
